import { useQuery } from '@tanstack/react-query';
import { inboxAgentService } from '@/shared/services/inbox-agent.service';
import { useWorkspaceStore } from '@/shared/stores/workspace.store';
import { usePlanGate } from '@/shared/hooks/usePlanGate';

/** Query key shared by every inbox-agent read and mutation. */
export const INBOX_AGENT_KEY = 'inbox-agent';

export function useWsId(): string | undefined {
  return useWorkspaceStore((s) => s.currentWorkspace?.id);
}

/**
 * Whether the workspace's plan opens an agent endpoint, so reads are only sent
 * where the API will answer them. The API gates these reads by plan; asking
 * anyway on a lower plan got a 403 on every page (the sidebar badge reads the
 * conversation states), and each 403 raised the global upgrade toast.
 * Mirrors the @RequiresPlan levels in apps/api/src/modules/inbox-agent.
 */
export function useAgentReadAllowed(level: 'classic' | 'agency'): boolean {
  const wsId = useWsId();
  const gate = usePlanGate(level);
  return !!wsId && !gate.isLoading && gate.allowed;
}

export function useAgentPolicy() {
  const wsId = useWsId();
  const allowed = useAgentReadAllowed('classic');
  return useQuery({ queryKey: [INBOX_AGENT_KEY, 'policy', wsId], queryFn: () => inboxAgentService.getPolicy(), enabled: allowed });
}

export function useEscalationLadder() {
  const wsId = useWsId();
  const allowed = useAgentReadAllowed('agency');
  return useQuery({ queryKey: [INBOX_AGENT_KEY, 'ladder', wsId], queryFn: () => inboxAgentService.getLadder(), enabled: allowed });
}

/**
 * The stored state of the given conversations (the API opens the read to
 * Classic). The list rows carry their own state; this is for a conversation
 * opened outside the loaded pages. Nothing is asked for no keys (IA-07).
 */
export function useConversationStates(keys: readonly string[] = []) {
  const wsId = useWsId();
  const allowed = useAgentReadAllowed('classic');
  const sorted = [...keys].sort();
  return useQuery({
    queryKey: [INBOX_AGENT_KEY, 'states', wsId, sorted],
    queryFn: () => inboxAgentService.getStates(sorted),
    enabled: allowed && sorted.length > 0,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
}

/** Notes and block flags per merged customer (M04). Shared so the list can hide blocked customers. */
export function useCustomerRecords() {
  const wsId = useWsId();
  const allowed = useAgentReadAllowed('agency');
  return useQuery({ queryKey: [INBOX_AGENT_KEY, 'customers', wsId], queryFn: () => inboxAgentService.getCustomers(), enabled: allowed });
}
